import React, { useState, useEffect } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Box,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditBetLevelsDialog from './EditBetLevelsDialog';
import AddBetConfigDialog from './AddBetConfigDialog';
import CustomPagination from '../../components/CustomPagination';
import Parse from 'parse';

const MICRO = 1000000;

const BetLevelsCell = ({ levels = [] }) => {
  if (levels.length === 0)
    return (
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
        —
      </Typography>
    );
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, maxWidth: 480 }}>
      {levels.map((v, i) => (
        <Chip
          key={i}
          label={v / MICRO}
          size="small"
          sx={{
            backgroundColor: 'rgba(255,193,7,0.12)',
            color: '#FFC107',
            fontSize: '10px',
            height: 20
          }}
        />
      ))}
    </Box>
  );
};

const formatCompactKeyPart = (value) => {
  if (typeof value === 'number') return String(value);
  return `"${String(value)}"`;
};

const formatPaytableForDisplay = (paytable = []) => {
  if (!Array.isArray(paytable) || paytable.length === 0) {
    return '-';
  }

  if (
    paytable.every(
      (entry) =>
        entry &&
        typeof entry === 'object' &&
        Array.isArray(entry.key) &&
        entry.key.length === 2 &&
        Object.prototype.hasOwnProperty.call(entry, 'value')
    )
  ) {
    return paytable
      .map(
        (entry) =>
          `(${formatCompactKeyPart(entry.key[0])}, ${formatCompactKeyPart(entry.key[1])}) : ${entry.value}`
      )
      .join('\n');
  }

  return JSON.stringify(paytable, null, 2);
};

const ActionButton = ({ record, onEditClick }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  return (
    <>
      <Button
        variant="contained"
        size="small"
        sx={{ textTransform: 'none' }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        Actions
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            onEditClick(record);
            setAnchorEl(null);
          }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit Configurations
        </MenuItem>
      </Menu>
    </>
  );
};

export const GameBetConfig = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [editRecord, setEditRecord] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [paytablePreview, setPaytablePreview] = useState(null);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (searchQuery !== debouncedSearch) {
        setPage(1); // Reset page on new search
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchData = async () => {
    setLoading(true);
    try {
      Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
      const Resource = Parse.Object.extend('GameBetConfig');
      const query = new Parse.Query(Resource);
      query.include('gameCatalogueId');

      if (debouncedSearch) {
        query.matches('gameName', debouncedSearch, 'i');
      }

      query.ascending('gameName');
      query.limit(1000); // Fetch all for client-side pagination

      const results = await query.find({ useMasterKey: true });
      setTotal(results.length);

      const formattedData = results.map((o) => ({
        id: o.id,
        gameSlug: o.get('gameSlug'),
        gameName: o.get('gameName'),
        paytable: o.get('paytable') || [],
        betLevels: o.get('betLevels') || [],
        minBet: o.get('minBet'),
        maxBet: o.get('maxBet'),
        defaultBetLevel: o.get('defaultBetLevel'),
        stepBet: o.get('stepBet'),
        updatedAt: o.get('updatedAt')
      }));

      // Client-side pagination
      const startIndex = (page - 1) * perPage;
      const paginatedData = formattedData.slice(startIndex, startIndex + perPage);

      setData(paginatedData);
    } catch (error) {
      console.error('Error fetching GameBetConfig:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, perPage, debouncedSearch]);

  const handleClose = (saved) => {
    setEditRecord(null);
    if (saved) fetchData();
  };

  const handleAddClose = (saved) => {
    setAddOpen(false);
    if (saved) fetchData();
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search game name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                </InputAdornment>
              )
            }}
            sx={{
              backgroundColor: '#333',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                '&.Mui-focused fieldset': { borderColor: '#FFC107' }
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            sx={{
              background: '#FFC107',
              color: '#0A0A0A',
              textTransform: 'none',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              '&:hover': { backgroundColor: '#FFA000' }
            }}
          >
            Add Config
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: '#242424',
          borderRadius: 2,
          boxShadow: 'none',
          backgroundImage: 'none',
          overflowX: 'auto'
        }}
      >
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Actions
              </TableCell>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Game Name
              </TableCell>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Slug
              </TableCell>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Default Bet
              </TableCell>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Min Bet
              </TableCell>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Max Bet
              </TableCell>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Paytable
              </TableCell>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Bet Levels
              </TableCell>
              <TableCell
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                Last Updated
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                  <CircularProgress sx={{ color: '#FFC107' }} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{ py: 4, color: 'rgba(255,255,255,0.5)', borderBottom: 'none' }}
                >
                  No game bet levels found.
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{
                    '&:last-child td': { borderBottom: 0 },
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.02)' }
                  }}
                >
                  <TableCell
                    sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    <ActionButton record={row} onEditClick={setEditRecord} />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {row.gameName}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {row.gameSlug}
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    {row.defaultBetLevel != null ? row.defaultBetLevel / MICRO : '—'}
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    {row.minBet != null ? row.minBet / MICRO : '—'}
                  </TableCell>
                  <TableCell
                    sx={{ color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                  >
                    {row.maxBet != null ? row.maxBet / MICRO : '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      whiteSpace: 'nowrap',
                      width: 120
                    }}
                  >
                    <IconButton
                      onClick={() =>
                        setPaytablePreview({
                          gameName: row.gameName,
                          paytable: row.paytable
                        })
                      }
                      size="small"
                      sx={{
                        color: '#FFC107',
                        border: '1px solid rgba(255,193,7,0.35)',
                        borderRadius: 1,
                        '&:hover': { backgroundColor: 'rgba(255,193,7,0.08)' }
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <BetLevelsCell levels={row.betLevels} />
                  </TableCell>
                  <TableCell
                    sx={{
                      color: '#fff',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {!loading && data.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CustomPagination
              page={page}
              perPage={perPage}
              total={total}
              setPage={setPage}
              setPerPage={setPerPage}
            />
          </Box>
        )}
      </TableContainer>

      <EditBetLevelsDialog open={Boolean(editRecord)} onClose={handleClose} record={editRecord} />
      <AddBetConfigDialog open={addOpen} onClose={handleAddClose} />
      <Dialog
        open={Boolean(paytablePreview)}
        onClose={() => setPaytablePreview(null)}
        PaperProps={{
          sx: {
            backgroundColor: '#2A2A2A',
            color: '#fff',
            minWidth: 640,
            maxWidth: '90vw'
          }
        }}
      >
        <DialogTitle sx={{ color: '#FFC107', fontWeight: 700 }}>
          Paytable Preview
          {paytablePreview?.gameName ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
              {paytablePreview.gameName}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              backgroundColor: '#1E1E1E',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 1.5,
              color: '#FFC107',
              fontSize: '13px',
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 420,
              overflowY: 'auto'
            }}
          >
            {formatPaytableForDisplay(paytablePreview?.paytable)}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPaytablePreview(null)}
            sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
